import { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Tooltip,
  Avatar,
  Badge,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  LogoutIcon,
  MenuIcon,
  SearchIcon,
  PersonIcon,
  PanelLeftCloseIcon,
  PanelLeftOpenIcon,
} from '@atlas/ui';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { SideMenu } from './SideMenu';
import { PreviewRoleBanner } from './PreviewRoleBanner';
import { MaintenanceBanner } from './MaintenanceBanner';
import { AsanaConnectBanner } from './AsanaConnectBanner';
import { AdminSideMenu } from '../../pages/admin/AdminSideMenu';
import { DivisionSwitcher } from './DivisionSwitcher';
import { RouteLoading } from './RouteLoading';
import { NotificationBell } from '../notifications/NotificationBell';
import { TeamsChatPanel, TeamsChatToggle } from '../teamsChat/TeamsChatPanel';
import { useTeamsStatus } from '../../hooks/useTeamsChatSession';
import { api } from '../../services/api';

// Optional environment banner (e.g. "DEV", "STAGING") shown next to the ATLAS
// wordmark. Set VITE_ENV_LABEL at build time; empty/unset on prod hides it.
const ENV_LABEL = (import.meta.env.VITE_ENV_LABEL ?? '').trim();

interface LayoutProps {
  children: React.ReactNode;
}

/**
 * Application shell. Provides:
 * - The persistent side menu (md+) / temporary drawer (<md) navigation,
 * - A compact top AppBar with the menu toggle (mobile), search trigger,
 *   notification-bell slot, theme toggle, and user chip,
 * - The `<main id="main">` landmark and skip-to-content link preserved from A1.
 */
const SIDE_MENU_COLLAPSED_KEY = 'atlas:side-menu-collapsed';

export function Layout({ children }: LayoutProps) {
  const { t } = useTranslation();
  const { user, logout, isAuthenticated } = useAuth();
  const theme = useTheme();
  const isPermanentRail = useMediaQuery(theme.breakpoints.up('md'));
  const location = useLocation();
  // While in the admin area the primary nav is swapped for the admin menu
  // (single sidebar, full-width tool content). The admin menu doesn't collapse.
  const isAdmin =
    location.pathname === '/admin' || location.pathname.startsWith('/admin/');

  // Asana connection status for the AppBar dot. Skip when unauthenticated to
  // avoid a 401 burst on the login page.
  const { data: asanaConnection } = useQuery({
    queryKey: ['asana-connection'],
    queryFn: () => api.asana.getConnection(),
    enabled: isAuthenticated,
    staleTime: 60_000,
  });
  const asanaConnected = asanaConnection?.connected ?? false;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SIDE_MENU_COLLAPSED_KEY) === '1';
    } catch {
      return false;
    }
  });
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const [searchFocusRequestId, setSearchFocusRequestId] = useState(0);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const userMenuOpen = Boolean(userMenuAnchor);

  const initials = user?.name
    ? user.name
        .split(' ')
        .map((part) => part[0])
        .filter(Boolean)
        .slice(0, 2)
        .join('')
        .toUpperCase()
    : '';

  // The user's own Teams profile photo, proxied so the `<img>` carries auth.
  // Only attempted when the connection's token actually grants photo reads —
  // otherwise the request is refused and MUI falls back to the initials anyway,
  // but gating avoids a guaranteed-to-fail fetch on every render. See TeamsAvatar.
  const { avatarsAvailable } = useTeamsStatus(isAuthenticated);
  const avatarSrc =
    avatarsAvailable && user?.entraOid
      ? `/api/v1/teams/avatars/${encodeURIComponent(user.entraOid)}`
      : undefined;

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDE_MENU_COLLAPSED_KEY, next ? '1' : '0');
      } catch {
        // ignore — localStorage may be unavailable (Safari private mode)
      }
      return next;
    });
  }, []);

  /**
   * Close the mobile drawer and return focus to the hamburger trigger.
   * Required for keyboard / screen-reader users — otherwise focus falls back
   * to <body> after the drawer's modal closes (WCAG 2.4.3).
   */
  const handleMobileClose = () => {
    setMobileOpen(false);
    // Defer until after the drawer's exit animation; otherwise MUI's
    // FocusTrap restoration competes with us and the focus ring vanishes.
    window.setTimeout(() => hamburgerRef.current?.focus(), 0);
  };

  /**
   * Asks the SideMenu to focus its search input. Ensures the search input is
   * actually visible first: opens the mobile drawer if we're sub-md, and
   * expands the rail if it's icon-collapsed on md+.
   */
  const requestSearchFocus = useCallback(() => {
    if (!isPermanentRail) {
      setMobileOpen(true);
    } else if (collapsed) {
      setCollapsed(false);
      try {
        localStorage.setItem(SIDE_MENU_COLLAPSED_KEY, '0');
      } catch {
        // ignore
      }
    }
    setSearchFocusRequestId((id) => id + 1);
  }, [isPermanentRail, collapsed]);

  // Global Cmd-K / Ctrl-K shortcut → focus the sidebar search input.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const isShortcut =
        (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k';
      if (!isShortcut) return;
      const target = event.target as HTMLElement | null;
      if (target?.isContentEditable) return;
      event.preventDefault();
      requestSearchFocus();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [requestSearchFocus]);

  return (
    <Box sx={{ display: 'flex', minHeight: '100dvh' }}>
      <Box
        component="a"
        href="#main"
        sx={{
          position: 'absolute',
          left: -10000,
          top: 'auto',
          width: 1,
          height: 1,
          overflow: 'hidden',
          zIndex: (t) => t.zIndex.tooltip + 1,
          '&:focus': {
            left: 8,
            top: 8,
            width: 'auto',
            height: 'auto',
            px: 2,
            py: 1,
            bgcolor: 'primary.main',
            color: 'common.white',
            borderRadius: 1,
            textDecoration: 'none',
            outline: '2px solid',
            outlineColor: 'primary.dark',
            outlineOffset: 2,
          },
        }}
      >
        {t('nav.skipToMain')}
      </Box>

      <AppBar
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{
          zIndex: (t) => t.zIndex.drawer + 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Toolbar variant="dense" sx={{ gap: 1, minHeight: 56 }}>
          {/* LEFT zone — hamburger (mobile) + brand */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <IconButton
              ref={hamburgerRef}
              onClick={() => setMobileOpen(true)}
              aria-label={t('nav.openNavigation')}
              aria-expanded={mobileOpen}
              aria-controls="primary-navigation"
              size="medium"
              edge="start"
              sx={{ display: { md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>

            {!isAdmin && (
              <Tooltip
                title={collapsed ? t('nav.expandSideMenu') : t('nav.collapseSideMenu')}
                arrow
              >
                <IconButton
                  onClick={toggleCollapsed}
                  aria-label={collapsed ? t('nav.expandSideMenu') : t('nav.collapseSideMenu')}
                  aria-controls="primary-navigation"
                  aria-expanded={!collapsed}
                  size="medium"
                  edge="start"
                  sx={{ display: { xs: 'none', md: 'inline-flex' } }}
                >
                  {collapsed ? <PanelLeftOpenIcon /> : <PanelLeftCloseIcon />}
                </IconButton>
              </Tooltip>
            )}

            <Box
              component={Link}
              to="/"
              aria-label="ATLAS"
              sx={{
                display: 'inline-flex',
                ml: isPermanentRail ? 0 : 0.5,
                '&:hover': { opacity: 0.85 },
              }}
            >
              {/* Two files, not one recolourable SVG: the wordmark flips from ink
                  to plate between themes, and the glyph plate does not. 40px is
                  what makes the tagline legible in a 56px dense toolbar — see the
                  reproportioning note in the SVGs. That costs width (~207px), so
                  the lockup is md+ only and everything narrower gets the glyph. */}
              <Box
                component="img"
                // ?v=2 is a one-time cache-buster, not a pattern to copy. The
                // first lockup shipped while nginx still marked unhashed assets
                // immutable-for-a-year, so every browser that loaded ATLAS on
                // 2026-08-11 holds the old artwork and will never revalidate it.
                // A new URL is the only thing that reaches them; index.html is
                // never cached, so this propagates on next load. Future edits
                // need no bump — the assets now revalidate.
                src={
                  theme.palette.mode === 'dark'
                    ? '/atlas-lockup-dark.svg?v=2'
                    : '/atlas-lockup-light.svg?v=2'
                }
                alt=""
                sx={{ height: 40, width: 'auto', display: { xs: 'none', md: 'block' } }}
              />
              <Box
                component="img"
                src="/atlas-tile.svg"
                alt=""
                sx={{ height: 32, width: 32, display: { xs: 'block', md: 'none' } }}
              />
            </Box>

            {ENV_LABEL && (
              <Chip
                label={ENV_LABEL}
                size="small"
                color="error"
                aria-label={`Environment: ${ENV_LABEL}`}
                sx={{ fontWeight: 700, letterSpacing: '0.05em', height: 20 }}
              />
            )}

            {isAuthenticated && (
              <Box sx={{ display: { xs: 'none', sm: 'inline-flex' }, ml: 0.5 }}>
                <DivisionSwitcher />
              </Box>
            )}
          </Box>

          {/* Flex spacer — search moved into the side menu */}
          <Box sx={{ flex: 1 }} />

          {/* RIGHT zone — utilities */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            {/* Mobile-only search trigger — desktop search lives in the side menu */}
            <Tooltip title={t('nav.openSearch')}>
              <IconButton
                onClick={requestSearchFocus}
                aria-label={t('nav.openSearch')}
                size="medium"
                sx={{ display: { xs: 'inline-flex', md: 'none' } }}
              >
                <SearchIcon />
              </IconButton>
            </Tooltip>

            {/* Notification bell slot — populated by Wave B5. Contract: docs/contracts/notification-bell-slot.md */}
            <Box
              data-slot="notification-bell"
              sx={{ display: 'inline-flex', alignItems: 'center', minWidth: 0 }}
            >
              {isAuthenticated && <NotificationBell />}
            </Box>

            {/* Teams chat sidebar toggle — self-hides when Teams isn't configured */}
            {isAuthenticated && <TeamsChatToggle />}

            {isAuthenticated && user && (
              <>
                <Tooltip
                  title={asanaConnected
                    ? t('nav.asanaConnectedStatus', { name: user.name })
                    : t('nav.asanaNotConnectedStatus', { name: user.name })}
                >
                  <Button
                    onClick={(e) => setUserMenuAnchor(e.currentTarget)}
                    aria-label={t('nav.openUserMenu')}
                    aria-haspopup="menu"
                    aria-expanded={userMenuOpen}
                    aria-controls={userMenuOpen ? 'user-menu' : undefined}
                    sx={{
                      ml: 0.5,
                      px: { xs: 0.5, md: 1 },
                      py: 0.5,
                      minWidth: 0,
                      color: 'text.primary',
                      textTransform: 'none',
                      borderRadius: 1,
                      gap: 1,
                    }}
                  >
                    <Badge
                      overlap="circular"
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      badgeContent={
                        <Box
                          aria-hidden
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: asanaConnected ? 'success.main' : 'error.main',
                            border: 2,
                            borderColor: 'background.paper',
                          }}
                        />
                      }
                    >
                      <Avatar
                        src={avatarSrc}
                        sx={{ width: 32, height: 32, fontSize: '0.875rem', bgcolor: 'primary.main' }}
                      >
                        {initials || <PersonIcon fontSize="small" />}
                      </Avatar>
                    </Badge>
                    <Box sx={{ display: { xs: 'none', md: 'block' }, textAlign: 'left' }}>
                      <Typography variant="body2" fontWeight={500} sx={{ lineHeight: 1.2 }}>
                        {user.name}
                      </Typography>
                    </Box>
                  </Button>
                </Tooltip>

                <Menu
                  id="user-menu"
                  anchorEl={userMenuAnchor}
                  open={userMenuOpen}
                  onClose={() => setUserMenuAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  slotProps={{ paper: { sx: { minWidth: 200, mt: 0.5 } } }}
                >
                  <MenuItem
                    component={Link}
                    to="/profile"
                    onClick={() => setUserMenuAnchor(null)}
                  >
                    <ListItemIcon>
                      <PersonIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t('nav.userMenuProfile')}</ListItemText>
                    <Box
                      aria-label={asanaConnected ? t('nav.asanaConnectedDot') : t('nav.asanaNotConnectedDot')}
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: asanaConnected ? 'success.main' : 'error.main',
                        ml: 1,
                      }}
                    />
                  </MenuItem>
                  <Divider />
                  <MenuItem
                    onClick={() => {
                      setUserMenuAnchor(null);
                      void logout();
                    }}
                  >
                    <ListItemIcon>
                      <LogoutIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t('nav.userMenuLogout')}</ListItemText>
                  </MenuItem>
                </Menu>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Side menu — the admin area swaps in its own menu (single sidebar,
          full-width content); everywhere else uses the primary nav. Permanent
          on md+ (with collapse), temporary on <md. */}
      {isAdmin ? (
        <AdminSideMenu
          open={mobileOpen}
          onClose={handleMobileClose}
          permanent={isPermanentRail}
        />
      ) : (
        <SideMenu
          open={mobileOpen}
          onClose={handleMobileClose}
          permanent={isPermanentRail}
          collapsed={collapsed}
          searchFocusRequestId={searchFocusRequestId}
          onRequestSearchFocus={requestSearchFocus}
        />
      )}

      <Box
        component="main"
        id="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          bgcolor: 'background.default',
          mt: '56px',
          py: 3,
          px: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <MaintenanceBanner />
        <PreviewRoleBanner />
        {isAuthenticated && <AsanaConnectBanner />}
        {/* Page chunks load lazily (see App.tsx); the shell stays put meanwhile. */}
        <Suspense fallback={<RouteLoading />}>{children}</Suspense>
      </Box>

      {/* Right-hand Teams chat drawer. Mounted here — a sibling of <main>,
          OUTSIDE the router outlet — so route changes neither unmount it nor
          reset which conversation is open. */}
      {isAuthenticated && <TeamsChatPanel />}
    </Box>
  );
}
